// src/pages/dashboard/Transfers.tsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { walletTransactionService } from '../../services/api/wallet-transaction.service';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  BitcoinIcon,
  EthereumIcon,
  TetherIcon,
  BinanceIcon,
  TronIcon
} from '../../components/ui/Icons';
import '../../assets/styles/transfers-button.css'; // Importamos los estilos de botones
import TransferWithdraw from "./transfer/transferWithdraw";
import TransferInternal from "./transfer/transferInternal";
import logo from '../../assets/images/logonegro.svg'
import { useWindow } from '../../context/WindowContext';
import { getCurrentBaseUrl } from '../../config/server.config';
import { useBitcoinFees, useEthereumFees, useBnbFees, useTronFees, useCryptoWallets } from '../../hooks/hooks'; // Removed useRailCounterpartyData - Rail v1 deprecated
import type { AppDispatch, RootState } from '../../store';
import { getNetworkFees } from "../../store/settingReducer";
import { getServerUrl } from '../../config/server.config';

interface Wallet {
  currency: string,
  address: string,
  balance: string
}
export default function Transfers() {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { windowWidth } = useWindow();
  const params = new URLSearchParams(location.search);
  const walletAddress = params.get('wallet');
  const walletCurrency = params.get('currency');
  const shouldShowWarning = params.get('showWarning') === 'true';
  const { data: { mainWallets, masterCard }, status } = useSelector((state: RootState) => state.assets);
  const { data: { networkFees } } = useSelector((state: RootState) => state.setting);
  const { data: counterPartys } = useSelector((state: RootState) => state.countrypartys);
  const { data: cryptoWalletsData } = useCryptoWallets();

  // Convert crypto wallets object to flat array
  type ExtendedWallet = Wallet & { network?: string; id?: string; userId?: string };

  const cryptoWallets = React.useMemo((): ExtendedWallet[] => {
    if (!cryptoWalletsData) return [];

    const walletsList: ExtendedWallet[] = [];

    // Process each network's wallets - same way as Wallets.tsx
    Object.keys(cryptoWalletsData).forEach(network => {
      if (cryptoWalletsData[network] && Array.isArray(cryptoWalletsData[network])) {
        cryptoWalletsData[network].forEach((wallet: any) => {
          // Iterate over all balances in the wallet
          if (wallet.balances && typeof wallet.balances === 'object') {
            Object.keys(wallet.balances).forEach(currency => {
              walletsList.push({
                id: wallet.id,
                userId: wallet.userId,
                network: network,
                address: wallet.address,
                currency: currency,
                balance: wallet.balances[currency]
              });
            });
          }
        });
      }
    });

    return walletsList;
  }, [cryptoWalletsData]);
  const [amount, setAmount] = useState<string>('');
  const [destinationAddress, setDestinationAddress] = useState<string>('');
  const [selectedWallet, setSelectedWallet] = useState<string>();
  const [selectedCurrency, setSelectedCurrency] = useState<string>(walletCurrency || '');
  const [feeRate, setFeeRate] = useState<number | null>(null); 
  const [feeTron, setFeeTron] = useState<number | null>(null); 
  const [ethGasPrice, setEthGasPrice] = useState<string | null>(null); 
  const [bnbGasPrice, setBnbGasPrice] = useState<string | null>(null); 
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState('usd');
  const [selectWalletType, setSelectedWalletType] = useState<string>(walletCurrency ? "crypto" : "main");
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [wallet, setWallet] = useState<ExtendedWallet>({
    currency: '',
    address: '',
    balance: ''
  })
  const [isTransfer, setIsTransfer] = useState<boolean>(false);
  const [showCryptoWarning, setShowCryptoWarning] = useState<boolean>(false);

  // Estado para modal de gas insuficiente
  const [showInsufficientGasModal, setShowInsufficientGasModal] = useState<boolean>(false);
  const [gasModalData, setGasModalData] = useState<{
    nativeCurrency: string;
    currentBalance: number;
    requiredAmount: number;
    missingAmount: number;
    token: string;
    networkName: string;
  } | null>(null);

  // Get Zenus account from Redux store
  const { zenusAccount } = useSelector((state: RootState) => state.assets.data);
  const usdBalance = zenusAccount?.balance || 0;
  const selectWallet = (value: string) => {
    if (!value) return;

    const [currency, address] = value.split('|');

    if (cryptoWallets && Array.isArray(cryptoWallets)) {
      const selectWalletTmp = cryptoWallets.filter((w: any) => {
        return w.currency === currency && w.address === address;
      })
      if (selectWalletTmp.length > 0) {
        console.log('Selected wallet:', selectWalletTmp[0]);
        setSelectedWallet(value);
        setWallet(selectWalletTmp[0]);
        setSelectedCurrency(currency);
      }
    }
  }

  const getCurrencyIcon = (currency: string) => {
    switch (currency.toUpperCase()) {
      case 'BTC':
        return <BitcoinIcon size={20} color="#B3A369" />;
      case 'ETH':
        return <EthereumIcon size={20} color="#B3A369" />;
      case 'USDT_ERC20':
      case 'USDT':
        return <TetherIcon size={20} color="#26A17B" />;
      case 'USDT_TRON':
      case 'USDT_TRC20':
        return <TetherIcon size={20} color="#26A17B" />;
      case 'TRX':
        return <TronIcon size={20} color="#FF060A" />;
      case 'BNB':
        return <BinanceIcon size={20} color="#F3BA2F" />;
      default:
        return null;
    }
  };
  const formatBalance = (balance: string, currency: string) => {
    const numericBalance = parseFloat(balance || '0');
    if (currency === 'BTC') {
      if (numericBalance >= 0.099) {
        return {
          value: numericBalance.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }),
          unit: 'BTC'
        };
      }
      else {
        const mBtcBalance = numericBalance * 1000;
        return {
          value: mBtcBalance.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }),
          unit: 'mBTC'
        };
      }
    }
    return {
      value: numericBalance.toLocaleString(),
      unit: currency.split('_')[0]
    };
  };

  const formatCurrencyName = (currency: string) => {
    switch (currency.toLowerCase()) {
      case 'usdt_tron':
      case 'usdt_trc20':
        return 'USDT (TRC-20)';
      case 'usdt_erc20':
      case 'usdt':  // Default USDT on Ethereum
        return 'USDT (ERC-20)';
      case 'usdt_bep20':
        return 'USDT (BEP-20)';
      case 'usdt_spl':
        return 'USDT (SPL)';
      case 'usdc_erc20':
      case 'usdc':  // Default USDC on Ethereum
        return 'USDC (ERC-20)';
      case 'usdc_tron':
      case 'usdc_trc20':
        return 'USDC (TRC-20)';
      case 'usdc_bep20':
        return 'USDC (BEP-20)';
      case 'usdc_spl':
        return 'USDC (SPL)';
      default:
        return currency.toUpperCase().split('_')[0];
    }
  };
  const getUnitCurrency = (currency: string) => {
    switch (currency) {
      case 'USDT_TRON':
      case 'USDT_ERC20':
      case 'USDT_BEP20':
        return 'USDT';
      default:
        return currency;
    }
  };

  const confirmBalance = (currency: string, balance: string) => {
    const bal = parseFloat(balance);
    // Solo verificar que no sea NaN y que sea mayor que 0
    return !isNaN(bal) && bal > 0;
  }

  // Function to get native currency info and required gas amount
  const getNativeGasInfo = (currency: string, network?: string) => {
    const curr = currency.toUpperCase();
    const net = network?.toLowerCase();

    // Handle network-based detection for generic tokens (USDT, USDC without suffix)
    if (net === 'bnb' && (curr === 'USDT' || curr === 'USDC')) {
      return { native: 'BNB', estimatedGas: 0.001, networkName: 'Binance Smart Chain (BEP-20)', token: curr };
    }
    if (net === 'tron' && (curr === 'USDT' || curr === 'USDC')) {
      return { native: 'TRX', estimatedGas: 15, networkName: 'TRON (TRC-20)', token: curr };
    }
    if (net === 'ethereum' && (curr === 'USDT' || curr === 'USDC')) {
      return { native: 'ETH', estimatedGas: 0.003, networkName: 'Ethereum (ERC-20)', token: curr };
    }
    if (net === 'solana' && (curr === 'USDT' || curr === 'USDC')) {
      return { native: 'SOL', estimatedGas: 0.002, networkName: 'Solana (SPL)', token: curr };
    }

    // Map tokens to their native currencies (with suffixes)
    const gasMapping: { [key: string]: { native: string; estimatedGas: number; networkName: string } } = {
      // Ethereum and ERC-20 tokens
      'ETH': { native: 'ETH', estimatedGas: 0.002, networkName: 'Ethereum' },
      'USDT_ERC20': { native: 'ETH', estimatedGas: 0.003, networkName: 'Ethereum (ERC-20)' },
      'USDC_ERC20': { native: 'ETH', estimatedGas: 0.003, networkName: 'Ethereum (ERC-20)' },

      // Binance Smart Chain and BEP-20 tokens
      'BNB': { native: 'BNB', estimatedGas: 0.0005, networkName: 'Binance Smart Chain' },
      'USDT_BEP20': { native: 'BNB', estimatedGas: 0.001, networkName: 'Binance Smart Chain (BEP-20)' },
      'USDC_BEP20': { native: 'BNB', estimatedGas: 0.001, networkName: 'Binance Smart Chain (BEP-20)' },

      // TRON and TRC-20 tokens
      'TRX': { native: 'TRX', estimatedGas: 5, networkName: 'TRON' },
      'USDT_TRON': { native: 'TRX', estimatedGas: 15, networkName: 'TRON (TRC-20)' },
      'USDT_TRC20': { native: 'TRX', estimatedGas: 15, networkName: 'TRON (TRC-20)' },
      'USDC_TRC20': { native: 'TRX', estimatedGas: 15, networkName: 'TRON (TRC-20)' },

      // Solana and SPL tokens
      'SOL': { native: 'SOL', estimatedGas: 0.001, networkName: 'Solana' },
      'USDT_SPL': { native: 'SOL', estimatedGas: 0.002, networkName: 'Solana (SPL)' },
      'USDC_SPL': { native: 'SOL', estimatedGas: 0.002, networkName: 'Solana (SPL)' },

      // Bitcoin (native only)
      'BTC': { native: 'BTC', estimatedGas: 0, networkName: 'Bitcoin' }, // BTC fees are from the same wallet
    };

    const info = gasMapping[curr];
    if (info && info.native !== curr) {
      // Token requires native currency for gas
      return { ...info, token: curr }; // Use the actual currency name (e.g., USDT_TRC20)
    }
    return null; // No gas validation needed (native currency)
  };

  const handleShowConfirmation = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedWallet) {
      toast.error('Please select a source wallet');
      return;
    }
    if (!destinationAddress) {
      toast.error('Please enter a destination address');
      return;
    }

    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    // Validate gas balance for token transfers
    const gasInfo = getNativeGasInfo(wallet.currency, (wallet as any).network);
    if (gasInfo) {
      // Find native currency wallet - SIMPLE: same address, native currency (case insensitive)
      const nativeWallet = cryptoWallets.find((w: any) =>
        w.currency.toUpperCase() === gasInfo.native.toUpperCase() &&
        w.address === wallet.address
      );

      const currentBalance = nativeWallet ? parseFloat(nativeWallet.balance) : 0;

      if (currentBalance < gasInfo.estimatedGas) {
        const missingAmount = gasInfo.estimatedGas - currentBalance;

        setGasModalData({
          nativeCurrency: gasInfo.native,
          currentBalance: currentBalance,
          requiredAmount: gasInfo.estimatedGas,
          missingAmount: missingAmount,
          token: gasInfo.token,
          networkName: gasInfo.networkName
        });

        setShowInsufficientGasModal(true);
        return;
      }
    }

    setShowConfirmation(true);
  };

  const handleConfirmTransfer = async () => {
    setIsTransfer(true);
    try {
      const formData = {
      sourceWallet: wallet.address,
      destinationAddress,
      amount,
      currency: wallet.currency,
      feeRate: feeRate,
      fee: wallet.currency === 'TRX' || wallet.currency === 'USDT_TRON' || wallet.currency === 'USDT_TRC20' || wallet.currency === 'USDC_TRC20' ? feeTron : undefined,
      gasPrice: wallet.currency === 'ETH' || wallet.currency === 'USDT_ERC20' || wallet.currency === 'USDC_ERC20' ? ethGasPrice :
        wallet.currency === 'BNB' || wallet.currency === 'USDT_BEP20' || wallet.currency === 'USDC_BEP20' ? bnbGasPrice : undefined
    };
    const transferData = {
      toAddress: formData.destinationAddress,
      amount: formData.amount
    };

    if (formData.currency === 'BTC' && formData.feeRate) {
      (transferData as any).feeRate = formData.feeRate;
    }

    if ((formData.currency === 'USDT_TRON' || formData.currency === 'TRX') && formData.fee) {
      (transferData as any).fee = formData.fee;
    }

    // Handle Ethereum and ERC-20 tokens fees
    if ((formData.currency === 'ETH' || formData.currency === 'USDT_ERC20' || formData.currency === 'USDC_ERC20' ||
        (formData.currency === 'USDT' && (wallet as any).network === 'ethereum') ||
        (formData.currency === 'USDC' && (wallet as any).network === 'ethereum')) && formData.gasPrice) {
      if (networkFees?.eth) {
        if (formData.gasPrice === networkFees.eth.slow) {
          (transferData as any).gasOption = 'slow';
        } else if (formData.gasPrice === networkFees.eth.standard) {
          (transferData as any).gasOption = 'standard';
        } else if (formData.gasPrice === networkFees.eth.fast) {
          (transferData as any).gasOption = 'fast';
        } else {
          (transferData as any).customGasPrice = formData.gasPrice;
        }
      } else {
        (transferData as any).customGasPrice = formData.gasPrice;
      }
    }

    // Handle BNB and BEP-20 tokens fees
    if ((formData.currency === 'BNB' || formData.currency === 'USDT_BEP20' || formData.currency === 'USDC_BEP20' ||
        (formData.currency === 'USDT' && (wallet as any).network === 'bnb') ||
        (formData.currency === 'USDC' && (wallet as any).network === 'bnb')) && formData.gasPrice) {
      (transferData as any).gasPrice = formData.gasPrice;
    }

    if (formData.currency === 'TRX') {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const apiUrl = getCurrentBaseUrl();

      const response = await fetch(`${apiUrl}/api/crypto-web3/tron/wallet/${formData.sourceWallet}/transfer-trx`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(transferData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to transfer TRX');
      }

      const result = await response.json();
      if (!result.error) {
        toast.success('Transfer initiated successfully');
      }
      return
    }

    if (formData.currency === 'USDT_TRON' || formData.currency === 'USDT_TRC20' || formData.currency === 'USDC_TRC20') {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const apiUrl = getCurrentBaseUrl();

      const endpoint = formData.currency === 'USDC_TRC20'
        ? `/api/crypto-web3/tron/wallet/${formData.sourceWallet}/transfer-usdc`
        : `/api/crypto-web3/tron/wallet/${formData.sourceWallet}/transfer-usdt`;

      const response = await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(transferData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to transfer ${formData.currency}`);
      }

      const result = await response.json();
      if (!result.error) {
        toast.success('Transfer initiated successfully');
      }
      return
    }

    // Handle Solana and SPL tokens
    if (formData.currency === 'SOL' || formData.currency === 'USDT_SPL' || formData.currency === 'USDC_SPL') {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const apiUrl = getCurrentBaseUrl();

      let endpoint = '';
      if (formData.currency === 'SOL') {
        endpoint = `/api/crypto-web3/solana/wallet/${formData.sourceWallet}/transfer`;
      } else if (formData.currency === 'USDT_SPL') {
        endpoint = `/api/crypto-web3/solana/wallet/${formData.sourceWallet}/transfer-usdt`;
      } else if (formData.currency === 'USDC_SPL') {
        endpoint = `/api/crypto-web3/solana/wallet/${formData.sourceWallet}/transfer-usdc`;
      }

      const response = await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(transferData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to transfer on Solana');
      }

      const result = await response.json();
      if (!result.error) {
        toast.success('Transfer initiated successfully');
      }
      return
    }

    // Handle remaining currencies (BTC, ETH, BNB and their tokens)
    console.log('Transfer data:', { currency: formData.currency, wallet: formData.sourceWallet, data: transferData });

    try {
      const result = await walletTransactionService.transfer(
        formData.currency,
        formData.sourceWallet,
        transferData
      );
      if (!result.error) {
        toast.success('Transfer initiated successfully');
      } else {
        toast.error(result.message || 'Transfer failed');
      }
    } catch (error: any) {
      console.error('Transfer error:', error);
      toast.error(error.message || 'Failed to process transfer');
    }
    } catch (error: any) {
      console.error('Transfer failed:', error);
      toast.error(error.message || 'Transfer failed. Please try again.');
    } finally {
      setIsTransfer(false);
      setShowConfirmation(false);
      setWallet({
        currency: '',
        address: '',
        balance: ''
      });
    }
  };
  const formattedBalance = wallet.currency ? formatBalance(wallet.balance, wallet.currency) : { value: '0', unit: '' };
  const onClose = () => {
    setOpenModal(false);
  }
  const confrimActiveTab = (method: string) => {
    if (counterPartys?.length == 0) {
      setOpenModal(true);
    } else {
      setActiveTab(method);
    }
  }
  const createCounterparty = () => {
    setOpenModal(false);
    navigate('/dashboard/counterparties/create');
  }
  // Zenus account is now loaded from Redux store

  useEffect(() => {
    const init = async () => {
      if (cryptoWallets && Array.isArray(cryptoWallets)) {
        const trxWallet = cryptoWallets.filter((w: any) => {
          return w.currency === "TRX";
        })
        if (trxWallet.length > 0) {
          dispatch(getNetworkFees(trxWallet[0]?.address))
        } else {
          dispatch(getNetworkFees("0"))
        }

        // If coming from crypto wallet, preselect it
        if (walletCurrency && walletAddress) {
          // Find the wallet with matching currency and address
          const targetWallet = cryptoWallets.find((w: any) =>
            w.currency === walletCurrency && w.address === walletAddress
          );
          if (targetWallet) {
            selectWallet(`${walletCurrency}|${walletAddress}`);
          }
        }
      }
    }
    init();
  }, [cryptoWallets, walletCurrency, walletAddress])

  // Separate useEffect for handling the warning parameter
  useEffect(() => {
    // Show crypto warning when explicitly requested via URL parameter
    if (shouldShowWarning && walletCurrency && walletAddress) {
      setShowCryptoWarning(true);
    }
  }, [shouldShowWarning, walletCurrency, walletAddress])
  
  return (
    <>
      <h1 className="text-3xl font-bold mb-5 w-full">{t('transfers.title')}</h1>
      <div className='mb-10 gap-2 flex'>
        <button
          onClick={() => { setSelectedWalletType("main") }}
          className="px-5 py-2.5 bg-[#ab9134] text-white border-none rounded cursor-pointer"
        >
          FIAT Wallets
        </button>
        <button
          onClick={() => {
            setSelectedWalletType("crypto");
            setShowCryptoWarning(true);
          }}
          className="px-5 py-2.5 bg-[#ab9134] text-white border-none rounded cursor-pointer"
        >
          Crypto Wallets
        </button>
      </div>
      {selectWalletType == "main" && (
        <div>
          {zenusAccount?.account ? (
            <div className="flex border-b border-gray-500 mb-6">
              <button
                className={`px-4 py-2 font-medium ${activeTab === 'usd' ? 'border-b-2 border-gray-500 text-gray-500' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('usd')}
              >
                USD Account Transfer
              </button>
            </div>
          ) : (
            <div className="mb-6">
              <p className="text-gray-600">No FIAT wallets available</p>
            </div>
          )}


          {status !== "succeeded" ? (
            <div className="text-center p-10">
              <p>Loading wallet information...</p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-7">
              {activeTab === 'usd' && zenusAccount?.account && (
                <div className="w-full max-w-2xl">
                  <div className="bg-white rounded-lg p-6 shadow-sm mb-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">USD Account Balance</h3>
                      <span className="text-2xl font-bold text-green-600">
                        ${usdBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      Account: ***{zenusAccount.account?.accountNumber?.slice(-4)}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Available Transfer Options:</strong>
                    </p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• <strong>ACH Transfer:</strong> US domestic transfers (2-3 business days)</li>
                      <li>• <strong>Wire Transfer (SWIFT):</strong> International transfers (1-3 business days)</li>
                      <li>• <strong>Internal Transfer:</strong> Instant transfers to other Belobaba users</li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-center text-gray-600">
                      To send money from your USD account, please visit the{' '}
                      <a href="/dashboard/wallets/usd" className="text-blue-600 hover:underline">
                        USD Account Details
                      </a>{' '}
                      page and click on the "Transfer" tab.
                    </p>
                  </div>
                </div>
              )}
              <div className="w-full sm:max-w-xs rounded-lg p-5">

              </div>
            </div>
          )}


        </div>
      )}
      {selectWalletType == "crypto" && (
        <div className='flex flex-wrap gap-7'>
          <div className='sm:w-[40%] w-full bg-white rounded-lg p-7 shadow-sm'>
            <form onSubmit={handleShowConfirmation}>
              <div className='mb-5'>
                <label
                  htmlFor="source-wallet"
                  className='block mb-2 text-sm text-[#666]'
                >
                  {t('transfers.selectWallet')}
                </label>
                <select
                  id="source-wallet"
                  value={selectedWallet}
                  onChange={(e) => {
                    selectWallet(e.target.value)
                  }}
                  className='w-full p-2.5 rounded-md border border-gray-300 text-base'
                  required
                >
                  <option value="" >{t('transfers.selectWallet')}</option>
                  {cryptoWallets && Array.isArray(cryptoWallets) && cryptoWallets.length > 0 ? (
                    cryptoWallets.map((wallet: any) => {
                      const formatted = formatBalance(wallet.balance, wallet.currency);
                      const displayCurrency = formatCurrencyName(wallet.currency);
                      const hasBalance = confirmBalance(wallet.currency, wallet.balance);

                      return (
                        <option
                          key={`${wallet.currency}-${wallet.address}`}
                          value={`${wallet.currency}|${wallet.address}`}
                          className='w-full'
                          disabled={!hasBalance}
                        >
                          {windowWidth < 450
                            ? `${displayCurrency} - ${formatted.value} ${formatted.unit} (${wallet.address.slice(-4)})${!hasBalance ? ' (No balance)' : ''}`
                            : `${displayCurrency} : ${formatted.value} ${formatted.unit} (${wallet.address.slice(0, 5)}...${wallet.address.slice(-4)})${!hasBalance ? ' (No balance)' : ''}`}
                        </option>
                      );
                    })
                  ) : (
                    <option value="" disabled>{t('wallets.noWalletsFound')}</option>
                  )}
                </select>
              </div>

              <div className='mb-5'>
                <label
                  htmlFor="destination-address"
                  className="block mb-2 text-sm text-gray-600"
                >
                  {t('transfers.recipientAddress')}
                </label>
                <input
                  id="destination-address"
                  type="text"
                  value={destinationAddress}
                  onChange={(e) => setDestinationAddress(e.target.value)}
                  className="w-full p-2.5 rounded-md border border-gray-300 text-base"
                  placeholder={t('transfers.recipientAddress')}
                  required
                />
              </div>

              <div className='mb-5'>
                <label
                  htmlFor="amount"
                  className='block mb-2 text-sm text-[#666]'
                >
                  {t('transfers.amount')}
                </label>
                <div className='flex items-center'>
                  <input
                    id="amount"
                    type="text"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className='w-full p-2.5 rounded-md border border-gray-300 text-base'
                    placeholder={t('transfers.amount')}
                    required
                  />

                  <div className='ml-2.5 font-bold flex items-center gap-1.5'>
                    {getCurrencyIcon(selectedCurrency)}
                    {selectedCurrency?.split('_')[0] || ''}
                  </div>
                </div>
                {/* <div className="p-2 bg-yellow-200 rounded-md mb-3 text-sm mt-3">

                 </div> */}
                {wallet && (
                  <div className='mt-2 text-sm text-gray-600 flex justify-between'>
                    <span>{t('transfers.availableBalance')}: {wallet.balance} {getUnitCurrency(wallet?.currency)}</span>
                    <button
                      type="button"
                      onClick={() => setAmount(wallet.balance)}
                      className='bg-transparent border-none text-belobaba-gold cursor-pointer text-sm'
                    >
                      Max
                    </button>
                  </div>
                )}
              </div>

              {(wallet?.currency === 'BTC' || wallet?.network === 'bitcoin') && networkFees?.btc && (
                <div className='mb-5'>
                  <label className='block mb-2 text-sm text-[#666]'>
                    Transaction Fee
                  </label>
                  <div className='flex gap-2.5 mb-2.5'>
                    <button
                      type="button"
                      onClick={() => setFeeRate(networkFees.btc?.slow?.satPerVbyte || 10)}
                      className={`flex-1 p-2.5 rounded-md border border-gray-300 cursor-pointer ${feeRate === (networkFees.btc?.slow?.satPerVbyte || 10) ? 'bg-gray-300' : 'bg-white'}`}
                    >
                      <div className="font-bold">Slow</div>
                      <div className='text-xs text-gray-600'>{networkFees.btc?.slow?.satPerVbyte || 10} sat/vByte</div>
                      <div className='text-xs text-gray-600'>{networkFees.btc?.slow?.estimatedConfirmation || '~60 mins'}</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setFeeRate(networkFees.btc?.medium?.satPerVbyte || 20)}
                      className={`flex-1 p-2.5 rounded-md border border-gray-300 cursor-pointer ${feeRate === (networkFees.btc?.medium?.satPerVbyte || 20) ? 'bg-gray-300' : 'bg-white'
                        }`}
                    >
                      <div className="font-bold">Medium</div>
                      <div className='text-xs text-gray-600'>{networkFees.btc?.medium?.satPerVbyte || 20} sat/vByte</div>
                      <div className='text-xs text-gray-600'>{networkFees.btc?.medium?.estimatedConfirmation || '~30 mins'}</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setFeeRate(networkFees.btc?.fast?.satPerVbyte || 30)}
                      className={`flex-1 p-2.5 rounded-md border border-gray-300 cursor-pointer ${feeRate === (networkFees.btc?.fast?.satPerVbyte || 30) ? 'bg-gray-300' : 'bg-white'
                        }`}
                    >
                      <div className="font-bold">Fast</div>
                      <div className='text-xs text-gray-600'>{networkFees.btc?.fast?.satPerVbyte || 30} sat/vByte</div>
                      <div className='text-xs text-gray-600'>{networkFees.btc?.fast?.estimatedConfirmation || '~10 mins'}</div>
                    </button>
                  </div>
                  <div className='text-xs text-gray-600'>
                    Estimated fee: {wallet.currency === 'BTC' && networkFees?.btc ?
                      (feeRate === networkFees.btc?.slow?.satPerVbyte ? networkFees.btc?.slow?.feeInBTC || '0.0001' :
                        feeRate === networkFees.btc?.medium?.satPerVbyte ? networkFees.btc?.medium?.feeInBTC || '0.0002' :
                          networkFees.btc?.fast?.feeInBTC || '0.0003') : '0.0001'} BTC
                  </div>
                </div>
              )}

              {(wallet?.network === 'ethereum' || wallet?.currency === 'ETH' || wallet?.currency === 'USDT_ERC20' || wallet?.currency === 'USDC_ERC20' || (wallet?.currency === 'USDT' && wallet?.network === 'ethereum') || (wallet?.currency === 'USDC' && wallet?.network === 'ethereum')) && networkFees?.eth && (
                <div className='mb-5'>
                  <label className='block mb-2 text-sm text-[#666]'>
                    Transaction Fee
                  </label>
                  <div className='flex-wrap justify-around flex gap-2.5 mb-2.5'>
                    <button
                      type="button"
                      onClick={() => setEthGasPrice(networkFees.eth?.slow || 10)}
                      className={`w-full sm:w-[30%] p-2.5 rounded-md border border-gray-300 cursor-pointer ${ethGasPrice === (networkFees.eth?.slow || 10) ? 'bg-gray-300' : 'bg-white'
                        }`}>
                      <div className="font-bold">Slow</div>
                      <div className='text-xs text-gray-600'>{networkFees.eth?.slow || 10} gwei</div>
                      <div className='text-xs text-gray-600'>~15 mins</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setEthGasPrice(networkFees.eth?.standard || 15)}
                      className={`w-full sm:w-[30%] p-2.5 rounded-md border border-gray-300 cursor-pointer ${ethGasPrice === (networkFees.eth?.standard || 15) ? 'bg-gray-300' : 'bg-white'
                        }`}
                    >
                      <div className="font-bold">Medium</div>
                      <div className='text-xs text-gray-600'>{networkFees.eth?.standard || 15} gwei</div>
                      <div className='text-xs text-gray-600'>~5 mins</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setEthGasPrice(networkFees.eth?.fast || 20)}
                      className={`w-full sm:w-[30%] p-2.5 rounded-md border border-gray-300 cursor-pointer ${ethGasPrice === (networkFees.eth?.fast || 20) ? 'bg-gray-300' : 'bg-white'
                        }`}>
                      <div className="font-bold">Fast</div>
                      <div className='text-xs text-gray-600'>{networkFees.eth?.fast || 20} gwei</div>
                      <div className='text-xs text-gray-600'>~2 mins</div>
                    </button>
                  </div>
                  <div className='text-xs text-gray-600'>
                    Estimated fee: {networkFees.eth?.estimatedCostStandard || '0.0006 ETH'}
                  </div>
                </div>
              )}

              {(wallet?.network === 'tron' || wallet?.currency === 'TRX' || wallet?.currency === 'USDT_TRON' || wallet?.currency === 'USDT_TRC20' || wallet?.currency === 'USDC_TRC20' || (wallet?.currency === 'USDT' && wallet?.network === 'tron')) && networkFees?.trx && (
                <div className='mb-5'>
                  <label className='block mb-2 text-sm text-[#666]'>
                    Transaction Fee
                  </label>
                  <div className='flex gap-2.5 mb-2.5'>
                    <button
                      type="button"
                      onClick={() => setFeeTron(networkFees.trx?.slow?.feeLimit || 5000000)}
                      className={`flex-1 p-2.5 rounded-md border border-gray-300 cursor-pointer ${feeTron === (networkFees.trx?.slow?.feeLimit || 5000000) ? 'bg-gray-300' : 'bg-white'
                        }`}>
                      <div className="font-bold">Slow</div>
                      <div className='text-xs text-gray-600'>{(networkFees.trx?.slow?.feeLimit || 5000000) / 1000000} TRX</div>
                      <div className='text-xs text-gray-600'>{networkFees.trx?.slow?.estimatedTimeMinutes || '~5 mins'}</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setFeeTron(networkFees.trx?.medium?.feeLimit || 10000000)}
                      className={`flex-1 p-2.5 rounded-md border border-gray-300 cursor-pointer ${feeTron === (networkFees.trx?.medium?.feeLimit || 10000000) ? 'bg-gray-300' : 'bg-white'
                        }`}>
                      <div className="font-bold">Medium</div>
                      <div className='text-xs text-gray-600'>{(networkFees.trx?.medium?.feeLimit || 10000000) / 1000000} TRX</div>
                      <div className='text-xs text-gray-600'>{networkFees.trx?.medium?.estimatedTimeMinutes || '~2 mins'}</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setFeeTron(networkFees.trx?.fast?.feeLimit || 30000000)}
                      className={`flex-1 p-2.5 rounded-md border border-gray-300 cursor-pointer ${feeTron === (networkFees.trx?.fast?.feeLimit || 30000000) ? 'bg-gray-300' : 'bg-white'
                        }`}
                    >
                      <div className="font-bold">Fast</div>
                      <div className='text-xs text-gray-600'>{(networkFees.trx?.fast?.feeLimit || 30000000) / 1000000} TRX</div>
                      <div className='text-xs text-gray-600'>{networkFees.trx?.fast?.estimatedTimeMinutes || '~1 min'}</div>
                    </button>
                  </div>
                  <div className='text-xs text-gray-600'>
                    Note: You need TRX in your wallet to pay for transaction fees.
                  </div>
                </div>
              )}

              {(wallet?.network === 'bnb' || wallet?.currency === 'BNB' || wallet?.currency === 'USDT_BEP20' || wallet?.currency === 'USDC_BEP20' || (wallet?.currency === 'USDT' && wallet?.network === 'bnb')) && networkFees?.bnb && (
                <div className='mb-5'>
                  <label className='block mb-2 text-sm text-[#666]'>
                    Transaction Fee
                  </label>
                  <div className='flex gap-2.5 mb-2.5'>
                    <button
                      type="button"
                      onClick={() => setBnbGasPrice(networkFees.bnb?.slow || 5)}
                      className={`flex-1 p-2.5 rounded-md border border-gray-300 cursor-pointer ${bnbGasPrice === (networkFees.bnb?.slow || 5) ? 'bg-gray-300' : 'bg-white'
                        }`}
                    >
                      <div className="font-bold">Slow</div>
                      <div className='text-xs text-gray-600'>{networkFees.bnb?.slow || 5} gwei</div>
                      <div className='text-xs text-gray-600'>~15 mins</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setBnbGasPrice(networkFees.bnb?.standard || 7)}
                      className={`flex-1 p-2.5 rounded-md border border-gray-300 cursor-pointer ${bnbGasPrice === (networkFees.bnb?.standard || 7) ? 'bg-gray-300' : 'bg-white'
                        }`}
                    >
                      <div className="font-bold">Medium</div>
                      <div className='text-xs text-gray-600'>{networkFees.bnb?.standard || 7} gwei</div>
                      <div className='text-xs text-gray-600'>~5 mins</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setBnbGasPrice(networkFees.bnb?.fast || 10)}
                      className={`flex-1 p-2.5 rounded-md border border-gray-300 cursor-pointer ${bnbGasPrice === (networkFees.bnb?.fast || 10) ? 'bg-gray-300' : 'bg-white'
                        }`}
                    >
                      <div className="font-bold">Fast</div>
                      <div className='text-xs text-gray-600'>{networkFees.bnb?.fast || 10} gwei</div>
                      <div className='text-xs text-gray-600'>~2 mins</div>
                    </button>
                  </div>
                  <div className='text-xs text-gray-600'>
                    Estimated fee: {networkFees.bnb?.estimatedCostStandard || '0.0003 BNB'}
                  </div>
                </div>
              )}

              {/* Solana Fees */}
              {(wallet?.network === 'solana' || wallet?.currency === 'SOL' || wallet?.currency === 'USDT_SPL' || wallet?.currency === 'USDC_SPL' || (wallet?.currency === 'USDT' && wallet?.network === 'solana')) && (
                <div className='mb-5'>
                  <label className='block mb-2 text-sm text-[#666]'>
                    Transaction Fee
                  </label>
                  <div className='p-2.5 bg-gray-100 rounded-md'>
                    <div className='text-sm text-gray-600'>
                      <strong>Standard Fee:</strong> ~0.00025 SOL
                    </div>
                    <div className='text-xs text-gray-500 mt-1'>
                      Solana has fixed low fees for all transactions
                    </div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="transfers-btn transfers-btn-default transfers-btn-full p-3 text-base mt-2.5"
              >
                {t('transfers.title')}
              </button>
            </form>
          </div>
        </div>
      )}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-7 rounded-lg w-11/12 max-w-lg">
            <h3 className="text-xl font-semibold mb-5 text-center flex items-center justify-center gap-2">
              <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {t('transfers.confirmTransfer')}
            </h3>

            {/* Network Information */}
            <div className="bg-gray-50 p-3 rounded-lg mb-4">
              <div className="text-xs text-gray-500 mb-1">Network</div>
              <div className="text-sm font-medium">
                {(() => {
                  // Use wallet.currency if selectedCurrency is not properly set
                  const currencyToCheck = (selectedCurrency || wallet?.currency || '').toUpperCase();

                  if (currencyToCheck === 'BTC' || currencyToCheck === 'BITCOIN') return 'Bitcoin';
                  if (currencyToCheck === 'ETH' || currencyToCheck === 'ETHEREUM') return 'Ethereum';
                  if (currencyToCheck === 'USDT_ERC20' || currencyToCheck === 'USDC_ERC20') return 'Ethereum (ERC-20)';
                  if (currencyToCheck === 'BNB' || currencyToCheck === 'BSC') return 'Binance Smart Chain';
                  if (currencyToCheck === 'USDT_BEP20' || currencyToCheck === 'USDC_BEP20') return 'Binance Smart Chain (BEP-20)';
                  if (currencyToCheck === 'TRX' || currencyToCheck === 'TRON') return 'TRON';
                  if (currencyToCheck === 'USDT_TRON' || currencyToCheck === 'USDT_TRC20' || currencyToCheck === 'USDC_TRC20') return 'TRON (TRC-20)';
                  if (currencyToCheck === 'SOL' || currencyToCheck === 'SOLANA') return 'Solana';
                  if (currencyToCheck === 'USDT_SPL' || currencyToCheck === 'USDC_SPL') return 'Solana (SPL)';

                  return 'Unknown Network';
                })()}
              </div>
            </div>

            <div className="space-y-3 mb-4">
              <div className="border-b pb-2">
                <div className="text-xs text-gray-500">From Wallet</div>
                <div className="text-sm font-mono break-all">{selectedWallet}</div>
              </div>
              <div className="border-b pb-2">
                <div className="text-xs text-gray-500">To Address</div>
                <div className="text-sm font-mono break-all">{destinationAddress}</div>
              </div>
              <div className="border-b pb-2">
                <div className="text-xs text-gray-500">Amount to Send</div>
                <div className="text-sm font-bold text-[#B3A369]">{amount} {formattedBalance?.unit || selectedCurrency?.split('_')[0]}</div>
              </div>

              {/* Transaction Fee Section */}
              <div className="border-b pb-2">
                <div className="text-xs text-gray-500">Network Fee</div>
                <div className="text-sm font-bold">
                  {selectedCurrency === 'BTC' && networkFees.btc && (
                    <>
                      {feeRate === networkFees.btc.slow.satPerVbyte ? networkFees.btc.slow.feeInBTC :
                        feeRate === networkFees.btc.medium.satPerVbyte ? networkFees.btc.medium.feeInBTC :
                          networkFees.btc.fast.feeInBTC} BTC
                      <span className="text-xs text-gray-500 ml-1">
                        ({feeRate === networkFees.btc.slow.satPerVbyte ? 'Slow' :
                          feeRate === networkFees.btc.medium.satPerVbyte ? 'Medium' : 'Fast'})
                      </span>
                    </>
                  )}
                  {(selectedCurrency === 'TRX' || selectedCurrency === 'USDT_TRON' || selectedCurrency === 'USDT_TRC20' || selectedCurrency === 'USDC_TRC20') && networkFees.trx && (
                    <>
                      {Number(feeTron) === networkFees.trx.slow.feeLimit ? (networkFees.trx.slow.feeLimit / 1000000) :
                        Number(feeTron) === networkFees.trx.medium.feeLimit ? (networkFees.trx.medium.feeLimit / 1000000) :
                          (networkFees.trx.fast.feeLimit / 1000000)} TRX
                      <span className="text-xs text-gray-500 ml-1">
                        ({Number(feeTron) === networkFees.trx.slow.feeLimit ? 'Slow' :
                          Number(feeTron) === networkFees.trx.medium.feeLimit ? 'Medium' : 'Fast'})
                      </span>
                    </>
                  )}
                  {(selectedCurrency === 'ETH' || selectedCurrency === 'USDT_ERC20' || selectedCurrency === 'USDC_ERC20') && networkFees.eth && (
                    <>
                      ~{(Number(ethGasPrice) * 21000 / 1000000000).toFixed(6)} ETH
                      <span className="text-xs text-gray-500 ml-1">({ethGasPrice} gwei)</span>
                    </>
                  )}
                  {(selectedCurrency === 'BNB' || selectedCurrency === 'USDT_BEP20' || selectedCurrency === 'USDC_BEP20') && networkFees.bnb && (
                    <>
                      ~{(Number(bnbGasPrice) * 21000 / 1000000000).toFixed(6)} BNB
                      <span className="text-xs text-gray-500 ml-1">({bnbGasPrice} gwei)</span>
                    </>
                  )}
                  {(selectedCurrency === 'SOL' || selectedCurrency === 'USDT_SPL' || selectedCurrency === 'USDC_SPL') && (
                    <>0.00025 SOL</>
                  )}
                </div>
              </div>

              {/* Total Cost Section */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-xs text-gray-500 mb-1">Total Cost</div>
                <div className="text-lg font-bold text-blue-600">
                  {selectedCurrency === 'BTC' && networkFees.btc && feeRate ? (
                    <>
                      {(parseFloat(amount || '0') + parseFloat(
                        feeRate === networkFees.btc.slow.satPerVbyte ? networkFees.btc.slow.feeInBTC :
                          feeRate === networkFees.btc.medium.satPerVbyte ? networkFees.btc.medium.feeInBTC :
                            networkFees.btc.fast.feeInBTC
                      )).toFixed(8)} BTC
                    </>
                  ) : (selectedCurrency === 'ETH' || selectedCurrency === 'USDT_ERC20' || selectedCurrency === 'USDC_ERC20') ? (
                    selectedCurrency === 'ETH' ?
                      `${(parseFloat(amount || '0') + (Number(ethGasPrice) * 21000 / 1000000000)).toFixed(8)} ETH` :
                      `${amount} ${selectedCurrency.split('_')[0]} + ${(Number(ethGasPrice) * 21000 / 1000000000).toFixed(6)} ETH fee`
                  ) : (selectedCurrency === 'BNB' || selectedCurrency === 'USDT_BEP20' || selectedCurrency === 'USDC_BEP20') ? (
                    selectedCurrency === 'BNB' ?
                      `${(parseFloat(amount || '0') + (Number(bnbGasPrice) * 21000 / 1000000000)).toFixed(8)} BNB` :
                      `${amount} ${selectedCurrency.split('_')[0]} + ${(Number(bnbGasPrice) * 21000 / 1000000000).toFixed(6)} BNB fee`
                  ) : (selectedCurrency === 'TRX' || selectedCurrency === 'USDT_TRON' || selectedCurrency === 'USDT_TRC20' || selectedCurrency === 'USDC_TRC20') ? (
                    selectedCurrency === 'TRX' ?
                      `${(parseFloat(amount || '0') + (Number(feeTron) / 1000000)).toFixed(6)} TRX` :
                      `${amount} ${selectedCurrency.split('_')[0]} + ${(Number(feeTron) / 1000000).toFixed(2)} TRX fee`
                  ) : (selectedCurrency === 'SOL' || selectedCurrency === 'USDT_SPL' || selectedCurrency === 'USDC_SPL') ? (
                    selectedCurrency === 'SOL' ?
                      `${(parseFloat(amount || '0') + 0.00025).toFixed(6)} SOL` :
                      `${amount} ${selectedCurrency.split('_')[0]} + 0.00025 SOL fee`
                  ) : `${amount} ${formattedBalance?.unit || selectedCurrency?.split('_')[0]}`}
                </div>
              </div>
            </div>

            {/* Enhanced Warning Section */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800 font-medium mb-2">⚠️ Important Warning</p>
              <ul className="text-xs text-amber-700 space-y-1">
                <li>• Cryptocurrency transactions are <strong>irreversible</strong></li>
                <li>• Once sent, funds <strong>cannot be recovered</strong></li>
                <li>• Double-check the <strong>recipient address</strong> is correct</li>
                <li>• Verify the <strong>network</strong> matches the recipient's wallet</li>
                <li>• Sending to wrong address or network = <strong>permanent loss</strong></li>
              </ul>
            </div>

            {/* Final Verification Box */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-5">
              <p className="text-xs text-red-700">
                <strong>Final Check:</strong> I confirm that the destination address <span className="font-mono">{destinationAddress?.slice(0, 10)}...{destinationAddress?.slice(-8)}</span> and network are correct.
              </p>
            </div>

            <div className="flex justify-between gap-2">
              <button
                onClick={() => setShowConfirmation(false)}
                className="flex-1 bg-gray-300 text-gray-800 p-2 rounded-md hover:bg-gray-400"
                disabled={isTransfer}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmTransfer}
                className="flex-1 bg-blue-500 text-white p-2 rounded-md hover:bg-blue-600 disabled:bg-gray-400"
                disabled={isTransfer}
              >
                {isTransfer ? t('common.processing') : t('transfers.confirmTransfer')}
              </button>
            </div>
          </div>
        </div>
      )}
      {openModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-belobaba-gold rounded-lg shadow-xl p-3 sm:p-6 w-full max-w-lg flex flex-col items-center relative">
            <img
              src={logo}
              alt="Login"
              className="absolute top-4 left-4 w-53 h-10 rounded-full"
            />

            <h2 className="w-full text-center mt-9 p-6 text-4xl text-white">You haven't counterparties for Withdraw. Do you want to create new counterparty now?</h2>
            <div className="flex flex-wrap gap-3">

              <button
                className="px-4 py-2 text-white bg-[#494949] rounded-md hover:bg-[#a39a22]"
                onClick={onClose}
              >
                No
              </button>
              <button
                className="px-4 py-2 text-white bg-[#75701f] rounded-md hover:bg-[#a39a22]"
                onClick={createCounterparty}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Insufficient Gas Modal */}
      {showInsufficientGasModal && gasModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-7 rounded-lg w-11/12 max-w-md">
            <div className="flex items-center justify-center mb-5">
              <svg className="w-16 h-16 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            <h3 className="text-2xl font-bold mb-4 text-center text-gray-800">
              {t('transfers.insufficientGasModal.title')}
            </h3>

            <p className="text-sm text-gray-600 mb-6 text-center">
              {t('transfers.insufficientGasModal.description', {
                token: gasModalData.token,
                network: gasModalData.networkName,
                nativeCurrency: gasModalData.nativeCurrency
              })}
            </p>

            <div className="space-y-4 mb-6">
              {/* Current Balance */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{t('transfers.insufficientGasModal.currentBalance')}:</span>
                  <span className="text-lg font-bold text-red-600">
                    {gasModalData.currentBalance.toFixed(6)} {gasModalData.nativeCurrency}
                  </span>
                </div>
              </div>

              {/* Required Amount */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{t('transfers.insufficientGasModal.requiredAmount')}:</span>
                  <span className="text-lg font-bold text-blue-600">
                    {gasModalData.requiredAmount.toFixed(6)} {gasModalData.nativeCurrency}
                  </span>
                </div>
              </div>

              {/* Missing Amount */}
              <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-700">{t('transfers.insufficientGasModal.missingAmount')}:</span>
                  <span className="text-xl font-bold text-amber-600">
                    {gasModalData.missingAmount.toFixed(6)} {gasModalData.nativeCurrency}
                  </span>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700 text-center">
                {t('transfers.insufficientGasModal.instructions', {
                  nativeCurrency: gasModalData.nativeCurrency
                })}
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={() => {
                setShowInsufficientGasModal(false);
                setGasModalData(null);
              }}
              className="w-full bg-[#ab9134] text-white p-3 rounded-md hover:bg-[#8a7329] font-bold text-base"
            >
              {t('transfers.insufficientGasModal.closeButton')}
            </button>
          </div>
        </div>
      )}

      {/* Crypto Warning Modal */}
      {showCryptoWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto m-4">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <svg className="h-8 w-8 text-yellow-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h2 className="text-2xl font-bold text-gray-800">⚠️ Important Crypto Transfer Information</h2>
              </div>
              <button
                onClick={() => setShowCryptoWarning(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4 text-gray-700">
              <div className="bg-yellow-50 p-3 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-yellow-800">🔥 Gas Fees Required</h3>
                <p className="text-sm mb-2">To transfer tokens (USDT, USDC), you must have sufficient native currency for gas fees:</p>
                <ul className="list-disc ml-5 text-sm space-y-1">
                  <li><strong>ERC-20 tokens</strong> require <strong>ETH</strong> for gas</li>
                  <li><strong>TRC-20 tokens</strong> require <strong>TRX</strong> for gas</li>
                  <li><strong>BEP-20 tokens</strong> require <strong>BNB</strong> for gas</li>
                  <li><strong>SPL tokens</strong> require <strong>SOL</strong> for gas</li>
                </ul>
              </div>

              <div className="bg-blue-50 p-3 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-blue-800">✅ Double-Check Before Sending</h3>
                <ul className="list-disc ml-5 text-sm space-y-1">
                  <li>Verify the recipient's wallet address is correct - <strong>transactions cannot be reversed</strong></li>
                  <li>Ensure you're using the correct network (sending to wrong network = <strong>permanent loss</strong>)</li>
                  <li>Test with a small amount first if unsure</li>
                </ul>
              </div>

              <div className="bg-red-50 p-3 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-red-800">🔒 Security Reminders</h3>
                <ul className="list-disc ml-5 text-sm space-y-1">
                  <li><strong>NEVER</strong> share your private keys or seed phrases with anyone</li>
                  <li>Store your recovery phrases in a secure, offline location</li>
                  <li>Beware of phishing sites and always verify URLs</li>
                  <li>Enable 2FA on all exchange accounts</li>
                </ul>
              </div>

              <div className="bg-purple-50 p-3 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-purple-800">🌐 Network Compatibility</h3>
                <ul className="list-disc ml-5 text-sm space-y-1">
                  <li><strong>BTC</strong> can only be sent to BTC addresses</li>
                  <li><strong>ETH/ERC-20</strong> tokens only to Ethereum addresses</li>
                  <li><strong>TRX/TRC-20</strong> tokens only to Tron addresses</li>
                  <li><strong>BNB/BEP-20</strong> tokens only to BSC addresses</li>
                  <li>Cross-chain transfers require bridges or exchanges</li>
                </ul>
              </div>

              <div className="bg-red-100 border-2 border-red-400 p-4 rounded-lg">
                <p className="font-bold text-red-800 text-center">
                  ⚠️ IMPORTANT: Cryptocurrency transactions are IRREVERSIBLE.<br/>
                  Once confirmed on the blockchain, they cannot be cancelled or refunded.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-center">
              <button
                onClick={() => setShowCryptoWarning(false)}
                className="px-6 py-3 bg-[#ab9134] text-white rounded-lg hover:bg-[#8a7329] font-bold"
              >
                I Understand - Continue to Crypto Transfers
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}